"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { ArrowLeft, UploadCloud, Download, Share2, ZoomIn, Move, Loader2 } from "lucide-react"
import { BADGE_W, BADGE_H, BADGE_ASSETS, BADGE_TEMPLATES, getBadgeTemplate, prepareBadge, drawBadge, loadPhoto, photoPlacement } from "./vizagBadgeCanvas"

const EDITOR_WIDTH = 240
const CLAMP = 1.5 // offsets are clamped to the actual photo bounds by photoPlacement
const SCALE = 2 // device-pixel scale for a crisp 2880x2160 export

function clamp(v, min, max) {
  return Math.min(max, Math.max(min, v))
}

export function ImAttendingPage() {
  useEffect(() => { document.title = "Create your badge | CTS 2026 Vizag" }, [])
  const [variant, setVariant] = useState("attending") // "attending" | "presenting"
  const [templateId, setTemplateId] = useState("twilight")
  const template = getBadgeTemplate(templateId)
  const photoFrame = template.photoFrame
  const editorHeight = EDITOR_WIDTH * photoFrame.height / photoFrame.width
  const [name, setName] = useState("")
  const [jobTitle, setJobTitle] = useState("")
  const [company, setCompany] = useState("")
  const [talk, setTalk] = useState("")
  const [photo, setPhoto] = useState(null)
  const [zoom, setZoom] = useState(1)
  const [pos, setPos] = useState({ x: 0, y: 0 })
  const [isDragOver, setIsDragOver] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [isPhotoLoading, setIsPhotoLoading] = useState(false)
  const [status, setStatus] = useState("")

  const fileInputRef = useRef(null)
  const dragState = useRef(null)
  const fileVersion = useRef(0)

  // Canvas badge — used for BOTH the live preview and the download, so what
  // you see is exactly what you get on every device.
  const canvasRef = useRef(null)
  const imgsRef = useRef(null)
  const photoImgRef = useRef(null)
  const [loadedTemplate, setLoadedTemplate] = useState(null)
  const [artworkError, setArtworkError] = useState(false)
  const ready = loadedTemplate === templateId
  const [photoVer, setPhotoVer] = useState(0)

  // Load the chosen artwork without resetting any of the user's details.
  useEffect(() => {
    let alive = true
    imgsRef.current = null
    setLoadedTemplate(null)
    setArtworkError(false)
    setStatus("")
    prepareBadge(templateId)
      .then((imgs) => {
        if (!alive) return
        imgsRef.current = { ...imgs, templateId }
        setLoadedTemplate(templateId)
      })
      .catch(() => {
        if (!alive) return
        setArtworkError(true)
        setStatus("The Vizag badge artwork could not load. Please refresh to try again.")
      })
    return () => {
      alive = false
    }
  }, [templateId])

  const redraw = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas || imgsRef.current?.templateId !== templateId) return
    if (canvas.width !== BADGE_W * SCALE) {
      canvas.width = BADGE_W * SCALE
      canvas.height = BADGE_H * SCALE
    }
    const ctx = canvas.getContext("2d")
    ctx.setTransform(SCALE, 0, 0, SCALE, 0, 0)
    drawBadge(
      ctx,
      {
        name,
        jobTitle,
        company,
        talk: variant === "presenting" ? talk : "",
        photoImg: photoImgRef.current,
        zoom,
        posX: pos.x,
        posY: pos.y,
        variant,
        templateId,
      },
      imgsRef.current,
    )
  }, [name, jobTitle, company, talk, zoom, pos.x, pos.y, variant, templateId])

  useEffect(() => {
    redraw()
  }, [redraw, ready, photoVer])

  const loadFile = useCallback((file) => {
    if (!file) return
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      setStatus("Please choose a JPG or PNG image.")
      return
    }
    if (file.size > 20 * 1024 * 1024) {
      setStatus("Please choose an image smaller than 20 MB.")
      return
    }
    const version = ++fileVersion.current
    setIsPhotoLoading(true)
    const reader = new FileReader()
    const fail = () => {
      if (version !== fileVersion.current) return
      setIsPhotoLoading(false)
      setStatus("This image could not be opened. Please choose a JPG or PNG.")
    }
    reader.onerror = fail
    reader.onload = (e) => {
      // Downscale large phone photos before use — keeps memory/redraw fast.
      const img = new Image()
      img.onload = async () => {
        if (version !== fileVersion.current) return
        const MAX = 1200
        const scale = Math.min(1, MAX / Math.max(img.width, img.height))
        const w = Math.round(img.width * scale)
        const h = Math.round(img.height * scale)
        const canvas = document.createElement("canvas")
        canvas.width = w
        canvas.height = h
        canvas.getContext("2d").drawImage(img, 0, 0, w, h)
        let out
        try {
          out = canvas.toDataURL("image/png")
        } catch {
          out = e.target.result
        }
        const decoded = await loadPhoto(out)
        if (version !== fileVersion.current) return
        if (!decoded) { fail(); return }
        photoImgRef.current = decoded
        setPhoto(out)
        setPhotoVer((v) => v + 1)
        setIsPhotoLoading(false)
        setZoom(1)
        setPos({ x: 0, y: 0 })
        setStatus("")
      }
      img.onerror = fail
      img.src = e.target.result
    }
    reader.readAsDataURL(file)
  }, [])

  const onDrop = (e) => {
    e.preventDefault()
    setIsDragOver(false)
    loadFile(e.dataTransfer.files?.[0])
  }

  /* ---- drag-to-reposition the crop ---- */
  const onPointerDown = (e) => {
    if (!photo) return
    e.currentTarget.setPointerCapture?.(e.pointerId)
    dragState.current = { startX: e.clientX, startY: e.clientY, baseX: pos.x, baseY: pos.y }
  }
  const onPointerMove = (e) => {
    if (!dragState.current) return
    const bounds = e.currentTarget.getBoundingClientRect()
    const dx = (e.clientX - dragState.current.startX) / (bounds.width || EDITOR_WIDTH)
    const dy = (e.clientY - dragState.current.startY) / (bounds.height || editorHeight)
    setPos({
      x: clamp(dragState.current.baseX + dx, -CLAMP, CLAMP),
      y: clamp(dragState.current.baseY + dy, -CLAMP, CLAMP),
    })
  }
  const onPointerUp = () => {
    dragState.current = null
  }

  /* ---- export (straight from the canvas — reliable on every device) ---- */
  function canvasToBlob() {
    redraw()
    return new Promise((resolve, reject) => canvasRef.current.toBlob((blob) => blob ? resolve(blob) : reject(new Error("Image export failed")), "image/png"))
  }

  const fileName = `cts2026-vizag-im-${variant}${templateId === "gold" ? "-navy-gold" : ""}.png`

  const handleDownload = async () => {
    setIsExporting(true)
    setStatus("Rendering your badge…")
    try {
      const blob = await canvasToBlob()
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.download = fileName
      link.href = url
      document.body.appendChild(link)
      link.click()
      link.remove()
      setTimeout(() => URL.revokeObjectURL(url), 4000)
      setStatus("Downloaded! Share it on your socials 🎉")
    } catch (err) {
      console.error(err)
      setStatus("Sorry, something went wrong while generating the image.")
    } finally {
      setIsExporting(false)
    }
  }

  const handleShare = async () => {
    setIsExporting(true)
    setStatus("Preparing to share…")
    try {
      const blob = await canvasToBlob()
      const file = new File([blob], fileName, { type: "image/png" })
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: variant === "presenting" ? "I'm presenting at CTS 2026" : "I'm attending CTS 2026",
          text:
            variant === "presenting"
              ? "I'll be presenting at the Candela Technology Summit 2026, Vizag. Come catch my session! #CTS2026"
              : "I'll be at the Candela Technology Summit 2026, Vizag. Let's connect! #CTS2026",
        })
        setStatus("Thanks for sharing! 🎉")
      } else {
        const url = URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.download = fileName
        link.href = url
        document.body.appendChild(link)
        link.click()
        link.remove()
        setTimeout(() => URL.revokeObjectURL(url), 4000)
        setStatus("Sharing isn't supported here — we downloaded the image instead.")
      }
    } catch (err) {
      if (err?.name !== "AbortError") {
        console.error(err)
        setStatus("Couldn't share the image. Try the Download button.")
      } else {
        setStatus("")
      }
    } finally {
      setIsExporting(false)
    }
  }

  const inputCls =
    "w-full rounded-xl border border-green-200 bg-white px-4 py-3 text-green-900 placeholder-green-400 outline-none transition focus:border-green-500 focus:ring-2 focus:ring-green-200"
  const crop = photoImgRef.current ? photoPlacement(photoImgRef.current, EDITOR_WIDTH, zoom, pos.x, pos.y, editorHeight) : null

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-green-100 bg-white/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3 sm:py-4">
          <Link to="/vizag-2026" className="flex items-center gap-2 text-green-700 transition hover:text-green-800">
            <ArrowLeft className="h-5 w-5" />
            <span className="text-sm font-medium sm:text-base">Back to CTS 2026</span>
          </Link>
          <img src={BADGE_ASSETS.cts} alt="CTS 2026 Vizag Edition" className="h-12 w-auto shrink-0 sm:h-16" />
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6 sm:py-8 md:py-10">
        <div className="mx-auto mb-6 max-w-2xl text-center sm:mb-8">
          <h1 className="text-2xl font-extrabold text-green-800 sm:text-3xl md:text-4xl">
            Create your “{variant === "presenting" ? "I'm Presenting" : "I'm Attending"}” badge
          </h1>
          <p className="mt-3 text-sm text-green-700 sm:text-base">
            Upload your photo, add your name, and download your personalized badge to post on LinkedIn, X and
            Instagram. {variant === "presenting"
              ? "Tell everyone you're on stage at the Candela Technology Summit 2026, Vizag!"
              : "Let's connect at the Candela Technology Summit 2026, Vizag!"}
          </p>

          {/* Badge type toggle */}
          <div
            role="radiogroup"
            aria-label="Badge type"
            onKeyDown={(e) => {
              if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.key)) {
                e.preventDefault()
                const next = variant === "attending" ? "presenting" : "attending"
                setVariant(next)
                e.currentTarget.querySelectorAll('[role="radio"]')[next === "attending" ? 0 : 1]?.focus()
              }
            }}
            className="mx-auto mt-6 inline-flex rounded-full border border-green-200 bg-white p-1 shadow-sm"
          >
            <button
              type="button"
              role="radio"
              aria-checked={variant === "attending"}
              tabIndex={variant === "attending" ? 0 : -1}
              onClick={() => setVariant("attending")}
              className={`min-h-[44px] rounded-full px-3 py-2 text-sm font-semibold transition sm:px-5 ${
                variant === "attending" ? "bg-green-600 text-white shadow" : "text-green-700 hover:bg-green-50"
              }`}
            >
              I&apos;m Attending
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={variant === "presenting"}
              tabIndex={variant === "presenting" ? 0 : -1}
              onClick={() => setVariant("presenting")}
              className={`min-h-[44px] rounded-full px-3 py-2 text-sm font-semibold transition sm:px-5 ${
                variant === "presenting" ? "bg-green-600 text-white shadow" : "text-green-700 hover:bg-green-50"
              }`}
            >
              I&apos;m Presenting
            </button>
          </div>
          <fieldset className="mx-auto mt-6 max-w-md">
            <legend className="mb-3 w-full text-sm font-semibold text-green-800">Choose a design</legend>
            <div className="grid grid-cols-2 gap-3">
              {BADGE_TEMPLATES.map((option) => (
                <label key={option.id} className="min-w-0 cursor-pointer">
                  <input
                    type="radio"
                    name="badge-design"
                    value={option.id}
                    checked={templateId === option.id}
                    onChange={() => setTemplateId(option.id)}
                    className="peer sr-only"
                  />
                  <span className="block overflow-hidden rounded-xl border-2 border-green-200 bg-white p-1 transition peer-checked:border-green-600 peer-checked:bg-green-50 peer-checked:ring-2 peer-checked:ring-green-600 peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-4 peer-focus-visible:outline-green-700 hover:border-green-500">
                    <img src={option.thumbnail} alt="" width="1448" height="1086" className="aspect-[4/3] w-full rounded-lg object-cover" />
                    <span className="flex min-h-[44px] items-center justify-center px-1 text-sm font-semibold text-green-800">{option.name}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
        </div>

        <div className="grid items-start gap-6 sm:gap-8 lg:grid-cols-[minmax(320px,400px)_minmax(0,1fr)]">
          {/* Preview comes first on mobile and sits beside the editor on desktop. */}
          <section id="badge-preview" className="min-w-0 scroll-mt-24 lg:sticky lg:top-28 lg:col-start-2 lg:row-start-1" aria-labelledby="badge-preview-heading">
            <h2 id="badge-preview-heading" className="mb-3 text-center text-lg font-bold text-green-800">Live preview</h2>
            <div className="relative mx-auto w-full max-w-[420px] lg:max-w-[600px]" aria-busy={!ready && !artworkError}>
              <canvas
                ref={canvasRef}
                className={`block h-auto w-full rounded-2xl shadow-xl ${ready ? "" : "invisible"}`}
                style={{ aspectRatio: `${BADGE_W} / ${BADGE_H}` }}
                aria-label={`Your I'm ${variant === "presenting" ? "Presenting" : "Attending"} badge preview — ${template.name}`}
              />
              {!ready && <div role="status" className="absolute inset-0 flex items-center justify-center rounded-2xl bg-green-100 p-6 text-center text-sm text-green-800">{artworkError ? "This design could not load. Choose another design or refresh to try again." : "Loading your badge design…"}</div>}
            </div>
            <p className="mt-3 text-center text-xs text-green-600 sm:text-sm">Download: 2880 × 2160 px</p>
          </section>

          {/* LEFT: form */}
          <section className="min-w-0 rounded-3xl border border-green-100 bg-white p-4 shadow-sm sm:p-6 lg:col-start-1 lg:row-start-1">
            {/* Photo upload */}
            <h2 className="mb-3 text-lg font-bold text-green-800">1. Your photo</h2>
            <div
              role="button"
              tabIndex={0}
              aria-label="Upload a photo. Drag and drop, or press Enter to browse."
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  fileInputRef.current?.click()
                }
              }}
              onDragOver={(e) => {
                e.preventDefault()
                setIsDragOver(true)
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={onDrop}
              className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-6 py-7 text-center transition focus:outline-none focus:ring-2 focus:ring-green-400 ${
                isDragOver ? "border-green-500 bg-green-50" : "border-green-200 hover:border-green-400 hover:bg-green-50/60"
              }`}
            >
              <UploadCloud className="h-8 w-8 text-green-500" />
              <p className="font-medium text-green-800">{photo ? "Change photo" : "Drag & drop your photo here"}</p>
              <p className="text-sm text-green-500">or click to browse — JPG or PNG</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png, image/jpeg, image/webp"
                aria-label="Choose your photo"
                className="hidden"
                onChange={(e) => { loadFile(e.target.files?.[0]); e.target.value = "" }}
              />
            </div>

            {/* Crop editor */}
            {photo && (
              <div className="mt-5 flex flex-col items-center gap-4">
                <div
                  onPointerDown={onPointerDown}
                  onPointerMove={onPointerMove}
                  onPointerUp={onPointerUp}
                  onPointerCancel={onPointerUp}
                  role="group"
                  tabIndex={0}
                  aria-label="Photo crop. Use arrow keys or drag to reposition."
                  onKeyDown={(e) => {
                    const offsets = { ArrowLeft: [-.03, 0], ArrowRight: [.03, 0], ArrowUp: [0, -.03], ArrowDown: [0, .03] }
                    if (!offsets[e.key]) return
                    e.preventDefault()
                    const [x, y] = offsets[e.key]
                    setPos((p) => ({ x: clamp(p.x + x, -CLAMP, CLAMP), y: clamp(p.y + y, -CLAMP, CLAMP) }))
                  }}
                  className="relative shrink-0 overflow-hidden bg-blue-100 ring-4 ring-sky-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-green-700"
                  style={{ width: EDITOR_WIDTH, maxWidth: "100%", aspectRatio: `${photoFrame.width} / ${photoFrame.height}`, borderRadius: `${photoFrame.radius / photoFrame.width * 100}% / ${photoFrame.radius / photoFrame.height * 100}%`, cursor: "grab", touchAction: "none" }}
                >
                  {crop && <img src={photo} alt="Your selected crop" draggable={false} className="pointer-events-none absolute max-w-none select-none" style={{ width: `${crop.width / EDITOR_WIDTH * 100}%`, height: `${crop.height / editorHeight * 100}%`, left: `${crop.x / EDITOR_WIDTH * 100}%`, top: `${crop.y / editorHeight * 100}%` }} />}
                </div>
                <div className="w-full">
                  <label htmlFor="zoom" className="flex items-center gap-2 text-sm font-medium text-green-700">
                    <ZoomIn className="h-4 w-4" /> Zoom
                  </label>
                  <input
                    id="zoom"
                    type="range"
                    min="1"
                    max="3"
                    step="0.01"
                    value={zoom}
                    onChange={(e) => setZoom(parseFloat(e.target.value))}
                    className="mt-2 w-full accent-green-600"
                  />
                  <p className="mt-2 flex items-center gap-1 text-xs text-green-500">
                    <Move className="h-3.5 w-3.5" /> Drag or use arrow keys to position your photo in the frame.
                  </p>
                  <button type="button" onClick={() => { setZoom(1); setPos({ x: 0, y: 0 }) }} className="mt-2 min-h-[44px] text-sm font-medium text-green-700 underline underline-offset-4">Reset crop</button>
                </div>
              </div>
            )}

            {/* Text fields */}
            <h2 className="mb-3 mt-8 text-lg font-bold text-green-800">2. Your details</h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="mb-1 block text-sm font-medium text-green-700">
                  Full name
                </label>
                <input
                  id="name"
                  className={inputCls}
                  placeholder="e.g. Priya Raman"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={36}
                />
              </div>
              <div>
                <label htmlFor="jobTitle" className="mb-1 block text-sm font-medium text-green-700">
                  Job title (optional)
                </label>
                <input
                  id="jobTitle"
                  className={inputCls}
                  placeholder="e.g. Wireless Systems Engineer"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  maxLength={48}
                />
              </div>
              {variant === "presenting" && (
                <div>
                  <label htmlFor="talk" className="mb-1 block text-sm font-medium text-green-700">
                    Talk / session title (optional)
                  </label>
                  <input
                    id="talk"
                    className={inputCls}
                    placeholder="e.g. Edge AI and WiFi APs"
                    value={talk}
                    onChange={(e) => setTalk(e.target.value)}
                    maxLength={90}
                  />
                </div>
              )}
              <div>
                <label htmlFor="company" className="mb-1 block text-sm font-medium text-green-700">
                  Company (optional)
                </label>
                <input
                  id="company"
                  className={inputCls}
                  placeholder="e.g. Candela Technologies"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  maxLength={48}
                />
              </div>
            </div>

            <a href="#badge-preview" className="mt-5 flex min-h-[44px] items-center justify-center text-sm font-medium text-green-700 underline underline-offset-4 lg:hidden">View updated badge</a>

            {/* Actions */}
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <button
                onClick={handleDownload}
                disabled={isExporting || !ready || isPhotoLoading}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-green-600 px-5 py-3 font-semibold text-white shadow-sm transition hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isExporting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Download className="h-5 w-5" />}
                Download PNG
              </button>
              <button
                onClick={handleShare}
                disabled={isExporting || !ready || isPhotoLoading}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border-2 border-green-600 px-5 py-3 font-semibold text-green-700 transition hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Share2 className="h-5 w-5" />
                Share
              </button>
            </div>
            <p className="mt-3 min-h-[1.25rem] text-center text-sm text-green-600" aria-live="polite">
              {status}
            </p>
          </section>

        </div>
      </main>
    </div>
  )
}
